import {
  Box,
  Container,
  Grid,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUrlFilterState, useUrlSortingState } from '../../../common/filtering/filters';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { getDripDashboard } from '../../../common/api/dripEmails';
import { DripEmailTable } from '../../../components/dashboard/DripEmailTable';
import { useUrlRangeState } from '../../../common/filtering/filters';
import { MetricComparison } from '../../../components/stats/metrics';
import {
  DripScopeFilter,
  useUrlDripScope,
} from '../../../components/dashboard/DripScopeFilter';

export default function View() {
  const [days, setDays] = useState<SelectProps.Option>({ label: 'Last 30 days', value: '30' });
  const daysNumber = days.value ? parseInt(days.value) : 30;
  const { query, setQuery } = useUrlFilterState();
  const { range, setRange } = useUrlRangeState(true);
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'scheduled_at',
    'desc',
  );
  const [now, setNow] = useState(new Date());
  const [textQuery, setTextQuery] = useState('');
  const { scope, setCampaignUuid, setZipBlastUuid } = useUrlDripScope();

  useEffect(() => {
    setRange({ key: `last-${daysNumber}-days`, amount: daysNumber, unit: 'day', type: 'relative' });
  }, [daysNumber]);

  const dashboardQuery = useQuery({
    queryKey: [
      'drip_dashboard',
      daysNumber,
      scope.campaign_uuid,
      scope.zip_blast_uuid,
    ] as const,
    queryFn: async () => {
      const data = await getDripDashboard(daysNumber, scope);
      if (!data) throw new Error('No dashboard data');
      return data;
    },
    // 10 minutes
    staleTime: 600_000,
  });

  const options: SelectProps.Options = [
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 60 days', value: '60' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Last 180 days', value: '180' },
  ];

  return (
    <DashboardViewLayout
      breadcrumbs={[{ text: 'Drip Campaign Dashboard', href: '' }]}
      headerVariant="high-contrast"
      header={
          <Container
          header={
            <Header
              variant="h1"
              actions={
                <SpaceBetween size="s" direction="horizontal" alignItems="end">
                  <DripScopeFilter
                    scope={scope}
                    setCampaignUuid={setCampaignUuid}
                    setZipBlastUuid={setZipBlastUuid}
                  />
                  <Select selectedOption={days} options={options} onChange={({ detail }) => setDays(detail.selectedOption)} />
                </SpaceBetween>
              }
              description={`Drip campaign metrics for the last ${daysNumber} days${scope.zip_blast_uuid ? ' · filtered to one zip blast' : scope.campaign_uuid ? ' · filtered to one campaign' : ''}`}
            >
              Drip Campaign Metrics
            </Header>
          }
          footer={
              <Grid
                  gridDefinition={[
                    { colspan: { default: 12, xs: 6, m: 3 } },
                    { colspan: { default: 12, xs: 6, m: 3 } },
                    { colspan: { default: 12, xs: 6, m: 3 } },
                    { colspan: { default: 12, xs: 6, m: 3 } },
                    { colspan: { default: 12, xs: 12, m: 12 } },
                  ]}
              >
                  <MetricComparison
                    header={
                      <DripStatisticHeader
                        description="Emails cancelled before being sent"
                      >
                        Cancelled
                      </DripStatisticHeader>
                  }
                  value={dashboardQuery.data?.current.cancelled}
                  previousValue={dashboardQuery.data?.prior.cancelled}
                  loading={dashboardQuery.isPending || dashboardQuery.isFetching}
                  unit="emails"
                  />
                  <MetricComparison
                  header={<DripStatisticHeader
                    description="Emails that failed to send"
                  >
                    Failed
                  </DripStatisticHeader>
                  }
                  value={dashboardQuery.data?.current.failed}
                  previousValue={dashboardQuery.data?.prior.failed}
                  loading={dashboardQuery.isPending || dashboardQuery.isFetching}
                  unit="emails"
                  />
                  <MetricComparison
                  header={<DripStatisticHeader
                    description="Emails that bounced"
                  >
                    Bounced
                  </DripStatisticHeader>
                  }
                  value={dashboardQuery.data?.current.bounced}
                  previousValue={dashboardQuery.data?.prior.bounced}
                  loading={dashboardQuery.isPending || dashboardQuery.isFetching}
                  unit="emails"
                  />
                  <MetricComparison
                  header={<DripStatisticHeader
                    description="Emails that were unsubscribed"
                  >
                    Unsubscribed
                  </DripStatisticHeader>
                  }
                  value={dashboardQuery.data?.current.unsubscribed}
                  previousValue={dashboardQuery.data?.prior.unsubscribed}
                  loading={dashboardQuery.isPending || dashboardQuery.isFetching}
                  unit="emails"
                  />
                  <MetricComparison
                  header={<DripStatisticHeader
                    description="Emails scheduled to send"
                  >
                    Future Scheduled
                  </DripStatisticHeader>
                  }
                  value={dashboardQuery.data?.future.scheduled}
                  loading={dashboardQuery.isPending || dashboardQuery.isFetching}
                  unit="emails"
                  />
              </Grid>
          }
        >
          <Grid
            gridDefinition={[
              { colspan: { default: 12, xs: 6, m: 3 } },
              { colspan: { default: 12, xs: 6, m: 3 } },
              { colspan: { default: 12, xs: 6, m: 3 } },
              { colspan: { default: 12, xs: 6, m: 3 } },
            ]}
          >
            <MetricComparison
              header={
                <DripStatisticHeader
                  description="Emails scheduled previously"
                >
                  Scheduled
                </DripStatisticHeader>
              }
              value={dashboardQuery.data?.current.scheduled}
              previousValue={dashboardQuery.data?.prior.scheduled}
              loading={dashboardQuery.isPending || dashboardQuery.isFetching}
              unit="emails"
            />
            <MetricComparison
              header={
                <DripStatisticHeader
                  description="Emails sent"
                >
                  Sent
                </DripStatisticHeader>
              }
              value={dashboardQuery.data?.current.sent}
              previousValue={dashboardQuery.data?.prior.sent}
              loading={dashboardQuery.isPending || dashboardQuery.isFetching}
              unit="emails"
            />
            <MetricComparison
              header={
                <DripStatisticHeader
                  description="Emails opened"
                >
                  Opened
                </DripStatisticHeader>
              }
              value={dashboardQuery.data?.current.opened}
              previousValue={dashboardQuery.data?.prior.opened}
              loading={dashboardQuery.isPending || dashboardQuery.isFetching}
              unit="emails"
            />
            <MetricComparison
              header={
                <DripStatisticHeader
                  description="Emails with links clicked"
                >
                  Clicked
                </DripStatisticHeader>
              }
              value={dashboardQuery.data?.current.clicked}
              previousValue={dashboardQuery.data?.prior.clicked}
              loading={dashboardQuery.isPending || dashboardQuery.isFetching}
              unit="emails"
            />
          </Grid>
        </Container>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        <Container header={<Header variant="h1">Recently Scheduled Drip Emails</Header>}>
          <DripEmailTable
              now={now}
              setNow={setNow}
              query={query}
              setQuery={setQuery}
              range={range}
              setRange={setRange}
              sortingField={sortingField}
              setSortingField={setSortingField}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              textQuery={textQuery}
              setTextQuery={setTextQuery}
              scope={scope}
              variant="embedded"
          />
        </Container>
      </SpaceBetween>
    </DashboardViewLayout>
  );
}

export type DripStatisticHeaderProps = {
  children: React.ReactNode;
  description?: React.ReactNode;
};

export function DripStatisticHeader(props: DripStatisticHeaderProps) {
  return (
    <Header variant="h2">
      <Box
        variant="span"
        color="text-status-info"
        fontSize="heading-l"
        textAlign="center"
        fontWeight="bold"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {props.children}
        </div>
      </Box>
      <Box
        variant="span"
        color="text-body-secondary"
        fontSize="heading-xs"
        textAlign="center"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {props.description}
        </div>
      </Box>
    </Header>
  )
}
